/**
 * WebhookFields — orchestrates all field renderers for the send_webhook action type.
 *
 * Sub-components (each in their own file to stay under ~200 lines):
 *  - WebhookUrlField    — text Input (required, scheme-validated)
 *  - HttpMethodField    — Select (POST/PUT/PATCH/GET)
 *  - HeadersEditorField — key/value row list (webhook-headers-field.tsx)
 *  - BodyTemplateField  — textarea + variable picker (webhook-body-field.tsx)
 */
import {
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { HeadersEditorField } from './WebhookHeadersField';
import { BodyTemplateField } from './WebhookBodyField';

const HTTP_METHODS = ['POST', 'PUT', 'PATCH', 'GET'] as const;

// ---- Props ----------------------------------------------------------------

interface WebhookFieldsProps {
  config: Record<string, unknown>;
  showErrors: boolean;
  onChange: (fieldName: string, value: unknown) => void;
  /** Atomic multi-field config update (used when applying a template sets body + headers together). */
  onPatch: (patch: Record<string, unknown>) => void;
}

// ---- Root component -------------------------------------------------------

export function WebhookFields({ config, showErrors, onChange, onPatch }: WebhookFieldsProps) {
  return (
    <>
      <WebhookUrlField
        value={String(config['url'] ?? '')}
        showErrors={showErrors}
        onChange={(v) => onChange('url', v)}
      />
      <HttpMethodField
        value={String(config['method'] ?? 'POST')}
        onChange={(v) => onChange('method', v)}
      />
      <HeadersEditorField
        headers={toStringRecord(config['headers'])}
        onChange={(v) => onChange('headers', v)}
      />
      <BodyTemplateField
        value={String(config['body_template'] ?? '')}
        currentHeaders={toStringRecord(config['headers'])}
        onChange={(v) => onChange('body_template', v)}
        onApplyTemplate={(body, mergedHeaders) =>
          onPatch({ body_template: body, headers: mergedHeaders })
        }
      />
    </>
  );
}

// ---- URL field ------------------------------------------------------------

function WebhookUrlField({
  value,
  showErrors,
  onChange,
}: {
  value: string;
  showErrors: boolean;
  onChange: (v: string) => void;
}) {
  const empty = showErrors && !value.trim();
  const badScheme = showErrors && !!value.trim() && !/^https?:\/\//i.test(value.trim());
  const urlError = empty
    ? 'URL is required.'
    : badScheme
      ? 'Must start with http:// or https://.'
      : undefined;
  return (
    <Field label="URL" required error={urlError}>
      {(p) => (
        <Input
          {...p}
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://hooks.example.com/alert"
          style={{ maxWidth: 480 }}
          invalid={!!urlError}
        />
      )}
    </Field>
  );
}

// ---- HTTP method Select ---------------------------------------------------

function HttpMethodField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const safe = (HTTP_METHODS as readonly string[]).includes(value) ? value : 'POST';
  return (
    <Field label="Method">
      {() => (
        <Select value={safe} onValueChange={onChange}>
          <SelectTrigger style={{ maxWidth: 140 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HTTP_METHODS.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  );
}

// ---- Helpers --------------------------------------------------------------

function toStringRecord(v: unknown): Record<string, string> {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const result: Record<string, string> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      result[k] = String(val ?? '');
    }
    return result;
  }
  return {};
}
